import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../api'
import { Link, navigate } from '../router'
import type { Billing, Cart } from '../shop'
import { EMPTY_BILLING, eur, shop, useCart, useShopConfig } from '../shop'
import { useMe } from '../state'
import { AuthForm } from './Auth'
import { Proofs } from './Proofs'

/** Preview that falls back to a placeholder while easydb is still processing the file. */
function Thumb({ src }: { src: string }) {
  const [failed, setFailed] = useState(false)
  return failed ? <span className="noimg">ohne Vorschau</span> : <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
}

const date = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '')

// --- cart ---------------------------------------------------------------------------------

export function CartPage() {
  const qc = useQueryClient()
  const { data: cart, isLoading } = useCart()
  const { data: conf } = useShopConfig()

  async function remove(id: number) {
    qc.setQueryData(['cart'], await shop.removeFromCart(id))
  }

  return (
    <main className="page wrap narrow">
      <h1>Warenkorb</h1>
      {isLoading && <p className="muted">Lade …</p>}
      {cart && cart.count === 0 && (
        <div className="none">
          <h3>Ihr Warenkorb ist leer.</h3>
          <p>Öffnen Sie ein Bild in der Recherche und wählen Sie »Lizenz berechnen«.</p>
          <Link to="/" className="btn">Zur Recherche</Link>
        </div>
      )}
      {cart && cart.count > 0 && (
        <>
          <ul className="lines">
            {cart.items.map((it) => (
              <li key={it.id} className="line">
                <Link to={`/lizenz/${it.asset_id}`} className="thumb"><Thumb src={it.thumb} /></Link>
                <div className="what">
                  <div className="mono muted small">{it.asset.inv}</div>
                  <div className="t">{it.asset.title}</div>
                  {it.price && <ul className="desc">{it.price.description.slice(0, 4).map((l) => <li key={l}>{l}</li>)}</ul>}
                  {it.error && <p className="err">{it.error}</p>}
                  {it.price?.by_agreement && <p className="err">Für diese Auswahl gibt es keinen Listenpreis. Bitte ändern oder anfragen.</p>}
                  <div className="acts">
                    <Link to={`/lizenz/${it.asset_id}?item=${it.id}`} className="linkbtn">Lizenz ändern</Link>
                    <button className="linkbtn" onClick={() => remove(it.id)}>Entfernen</button>
                  </div>
                </div>
                <div className="amt">{eur(it.price?.net_cents)}</div>
              </li>
            ))}
          </ul>
          <Totals t={cart} />
          <div className="cart-actions">
            <Link to="/" className="linkbtn">Weitere Bilder suchen</Link>
            <button className="btn primary" disabled={!cart.checkout_ready} onClick={() => navigate('/kasse')}>Zur Kasse</button>
          </div>
          {conf && !conf.enabled && <p className="banner info">Bestellungen sind derzeit nicht möglich.</p>}
        </>
      )}
    </main>
  )
}

function Totals({ t }: { t: Pick<Cart, 'net_cents' | 'vat_rate' | 'vat_cents' | 'gross_cents'> }) {
  return (
    <table className="totals">
      <tbody>
        <tr><td>Summe netto</td><td>{eur(t.net_cents)}</td></tr>
        <tr><td>Umsatzsteuer {t.vat_rate.toLocaleString('de-DE')} %</td><td>{eur(t.vat_cents)}</td></tr>
        <tr className="sum"><td>Gesamt</td><td>{eur(t.gross_cents)}</td></tr>
      </tbody>
    </table>
  )
}

// --- checkout -----------------------------------------------------------------------------

export function CheckoutPage() {
  const qc = useQueryClient()
  const { data: me } = useMe()
  const { data: cart } = useCart()
  const { data: conf } = useShopConfig()
  const account = useQuery({ queryKey: ['account'], queryFn: shop.account, enabled: !!me?.authenticated })
  const [b, setB] = useState<Billing>(EMPTY_BILLING)
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [accept, setAccept] = useState(false)
  const [waive, setWaive] = useState(false)
  const [save, setSave] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { if (account.data) setB({ ...EMPTY_BILLING, ...account.data }) }, [account.data])
  const set = (k: keyof Billing) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setB({ ...b, [k]: e.target.value })

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const order = await shop.checkout({ billing: b, reference, note, accept_terms: accept, waive_withdrawal: waive, save_profile: save })
      qc.invalidateQueries({ queryKey: ['cart'] })
      qc.invalidateQueries({ queryKey: ['orders'] })
      qc.setQueryData(['order', order.number], order)
      navigate(`/bestellungen/${order.number}?neu=1`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Die Bestellung konnte nicht abgeschlossen werden.')
    } finally {
      setBusy(false)
    }
  }

  if (cart && cart.count === 0) {
    return <main className="page wrap narrow"><h1>Kasse</h1><p>Der Warenkorb ist leer. <Link to="/" className="linkbtn">Zur Recherche</Link></p></main>
  }

  return (
    <main className="page wrap narrow">
      <h1>Kasse</h1>
      <ol className="progress"><li className="done">Warenkorb</li><li className={me?.authenticated ? 'done' : 'cur'}>Konto</li><li className={me?.authenticated ? 'cur' : ''}>Rechnung &amp; Bestellung</li></ol>
      {!me?.authenticated ? (
        <section className="box">
          <h2>Anmelden oder Konto anlegen</h2>
          <p className="muted">Ihr Warenkorb bleibt erhalten. Über das Konto erreichen Sie Rechnungen und Downloads.</p>
          <AuthForm initial="register" />
        </section>
      ) : (
        <form onSubmit={submit} className="checkout">
          <section className="box">
            <h2>Rechnungsadresse</h2>
            <div className="fgrid">
              <label className="span2">Firma / Organisation<input value={b.organisation} onChange={set('organisation')} autoComplete="organization" /></label>
              <label>Vorname<input value={b.first_name} onChange={set('first_name')} autoComplete="given-name" /></label>
              <label>Nachname<input value={b.last_name} onChange={set('last_name')} autoComplete="family-name" /></label>
              <label className="span2">Straße und Hausnummer *<input value={b.street} onChange={set('street')} required autoComplete="street-address" /></label>
              <label>PLZ *<input value={b.postal_code} onChange={set('postal_code')} required autoComplete="postal-code" /></label>
              <label>Ort *<input value={b.city} onChange={set('city')} required autoComplete="address-level2" /></label>
              <label>Land *
                <select value={b.country} onChange={set('country')} autoComplete="country">
                  {[['DE', 'Deutschland'], ['AT', 'Österreich'], ['CH', 'Schweiz'], ['NL', 'Niederlande'], ['FR', 'Frankreich'], ['IT', 'Italien'],
                    ['BE', 'Belgien'], ['DK', 'Dänemark'], ['PL', 'Polen'], ['LU', 'Luxemburg'], ['GB', 'Vereinigtes Königreich']].map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                </select>
              </label>
              <label>USt-IdNr. (Unternehmen)<input value={b.vat_id} onChange={set('vat_id')} /></label>
              <label>E-Mail für Rechnung *<input type="email" value={b.email} onChange={set('email')} required autoComplete="email" /></label>
              <label>Telefon<input value={b.phone} onChange={set('phone')} autoComplete="tel" /></label>
            </div>
            <label className="check"><input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} /><span>Adresse im Konto speichern</span></label>
          </section>

          <section className="box">
            <h2>Ihre Lizenzen</h2>
            <ul className="lines compact">
              {cart?.items.map((it) => (
                <li key={it.id} className="line">
                  <span className="thumb"><Thumb src={it.thumb} /></span>
                  <div className="what"><div className="t"><span className="mono">{it.asset.inv}</span> {it.asset.title}</div>
                    <div className="muted small">{it.price?.description[0]}</div></div>
                  <div className="amt">{eur(it.price?.net_cents)}</div>
                </li>
              ))}
            </ul>
            {cart && <Totals t={cart} />}
            <div className="fgrid">
              <label>Ihr Projekt / Bestellzeichen<input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="erscheint auf der Rechnung" /></label>
              <label>Anmerkung<input value={note} onChange={(e) => setNote(e.target.value)} /></label>
            </div>
          </section>

          <section className="box">
            <label className="check">
              <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} required />
              <span>Ich habe die Lizenzbedingungen{conf?.terms_url && <> und die <a href={conf.terms_url} target="_blank" rel="noreferrer">AGB</a></>} gelesen
                und akzeptiere sie. Die Nutzungsrechte werden mit vollständiger Zahlung wirksam.</span>
            </label>
            <label className="check">
              <input type="checkbox" checked={waive} onChange={(e) => setWaive(e.target.checked)} />
              <span>Ich möchte die Bilddateien sofort nach Zahlung herunterladen und weiß, dass mein Widerrufsrecht
                (für Verbraucher) damit vorzeitig erlischt.</span>
            </label>
            <p className="muted small">Sie erhalten sofort eine Rechnung. Bezahlen Sie per Überweisung{conf?.stripe ? ' oder online (Karte und weitere Zahlarten)' : ''}.
              {conf?.privacy_url && <> <a href={conf.privacy_url} target="_blank" rel="noreferrer">Datenschutz</a></>}</p>
            {error && <p className="err" role="alert">{error}</p>}
            <div className="cart-actions">
              <Link to="/warenkorb" className="linkbtn">Zurück zum Warenkorb</Link>
              <button className="btn primary" disabled={busy || !cart?.checkout_ready}>{busy ? 'Bestellung wird angelegt …' : `Zahlungspflichtig bestellen · ${eur(cart?.gross_cents)}`}</button>
            </div>
          </section>
        </form>
      )}
    </main>
  )
}

// --- order --------------------------------------------------------------------------------

export function OrderPage({ number, search }: { number: string; search: string }) {
  const qc = useQueryClient()
  const params = new URLSearchParams(search)
  const order = useQuery({ queryKey: ['order', number], queryFn: () => shop.order(number), retry: false })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)

  // back from Stripe: confirm right away (the webhook does the same server-to-server)
  useEffect(() => {
    const sid = params.get('session_id')
    if (params.get('zahlung') === 'ok' && sid) {
      shop.confirm(number, sid).then((o) => qc.setQueryData(['order', number], o)).catch(() => undefined)
    }
  }, [number])  // eslint-disable-line react-hooks/exhaustive-deps

  async function payOnline() {
    setBusy(true)
    setError(null)
    try {
      const { url } = await shop.stripe(number)
      window.location.href = url
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Online-Zahlung nicht möglich.')
      setBusy(false)
    }
  }

  function copy(label: string, text: string) {
    navigator.clipboard?.writeText(text).then(() => { setCopied(label); setTimeout(() => setCopied(null), 1600) }, () => undefined)
  }

  if (order.error) return <main className="page wrap narrow"><p className="banner">{(order.error as Error).message}</p></main>
  const o = order.data
  if (!o) return <main className="page wrap narrow"><p className="muted">Lade …</p></main>
  const fresh = params.get('neu') === '1'

  return (
    <main className="page wrap narrow">
      <div className="crumbs"><Link to="/konto" className="linkbtn">← Meine Bestellungen</Link></div>
      <h1>Bestellung <span className="mono">{o.number}</span> <span className={`status ${o.status}`}>{o.status_label}</span></h1>
      {fresh && o.status === 'awaiting_payment' && (
        <p className="banner ok">Vielen Dank für Ihre Bestellung! Die Rechnung {o.invoice_number} haben wir Ihnen per E-Mail geschickt.</p>
      )}
      {params.get('zahlung') === 'abgebrochen' && o.status === 'awaiting_payment' && <p className="banner info">Die Online-Zahlung wurde abgebrochen. Sie können es erneut versuchen oder überweisen.</p>}
      {params.get('zahlung') === 'ok' && o.status !== 'paid' && <p className="banner info">Zahlung wird bestätigt … Die Seite aktualisiert sich gleich.</p>}

      <div className="order-head">
        <div>
          <div className="label">Rechnung</div>
          <div><span className="mono">{o.invoice_number}</span> vom {date(o.invoice_date)}</div>
          <div className="doclinks">
            <a className="linkbtn" href={o.invoice_url} target="_blank" rel="noreferrer">Rechnung (PDF)</a>
            {o.cancellation_url && <a className="linkbtn" href={o.cancellation_url} target="_blank" rel="noreferrer">Stornorechnung (PDF)</a>}
          </div>
        </div>
        <div><div className="label">Betrag</div><div className="big">{eur(o.gross_cents)}</div><div className="muted small">netto {eur(o.net_cents)} + {o.vat_rate.toLocaleString('de-DE')} % USt.</div></div>
        <div><div className="label">{o.status === 'paid' ? 'Bezahlt' : 'Fällig'}</div><div>{o.status === 'paid' ? `${date(o.paid_at)} · ${o.payment_label}` : date(o.due_date)}</div></div>
      </div>

      {o.status === 'awaiting_payment' && (
        <section className="pay">
          {o.stripe && (
            <div className="paybox">
              <h2>Online bezahlen</h2>
              <p className="muted">Karte, Apple Pay, Google Pay, SEPA und weitere – sicher über Stripe. Downloads sind sofort danach verfügbar.</p>
              <button className="btn primary" onClick={payOnline} disabled={busy}>{busy ? 'Weiterleitung …' : `Jetzt ${eur(o.gross_cents)} bezahlen`}</button>
              {error && <p className="err">{error}</p>}
            </div>
          )}
          {o.bank && (
            <div className="paybox">
              <h2>Überweisung</h2>
              <dl className="bank">
                <dt>Empfänger</dt><dd>{o.bank.holder}</dd>
                <dt>IBAN</dt><dd><span className="mono">{o.bank.iban}</span> <button className="linkbtn small" onClick={() => copy('iban', o.bank!.iban.replace(/\s/g, ''))}>{copied === 'iban' ? 'kopiert' : 'kopieren'}</button></dd>
                {o.bank.bic && <><dt>BIC</dt><dd className="mono">{o.bank.bic}</dd></>}
                {o.bank.bank && <><dt>Bank</dt><dd>{o.bank.bank}</dd></>}
                <dt>Betrag</dt><dd>{eur(o.gross_cents)}</dd>
                <dt>Verwendungszweck</dt><dd><span className="mono">{o.bank.reference}</span> <button className="linkbtn small" onClick={() => copy('ref', o.bank!.reference)}>{copied === 'ref' ? 'kopiert' : 'kopieren'}</button></dd>
              </dl>
              <p className="muted small">Bitte bis {date(o.due_date)}. Sobald die Zahlung verbucht ist, erhalten Sie eine E-Mail und können die Dateien herunterladen.</p>
            </div>
          )}
          {!o.stripe && !o.bank && <p className="banner info">Wir senden Ihnen die Zahlungsinformationen per E-Mail.</p>}
        </section>
      )}

      <h2 className="sec">Lizenzen und Downloads</h2>
      <ul className="lines">
        {o.items.map((it) => (
          <li key={it.id} className="line">
            <span className="thumb"><Thumb src={it.thumb} /></span>
            <div className="what">
              <div className="mono muted small">{it.asset.inv}</div>
              <div className="t">{it.asset.title}</div>
              <ul className="desc">{it.description.map((l) => <li key={l}>{l}</li>)}</ul>
              <div className="acts">
                {it.download_url
                  ? <a className="btn primary" href={it.download_url}>Bilddatei herunterladen</a>
                  : <span className="muted small">{it.download_blocked}</span>}
                {it.downloads > 0 && <span className="muted small">{it.downloads}× geladen</span>}
              </div>
            </div>
            <div className="amt">{eur(it.net_cents)}</div>
          </li>
        ))}
      </ul>
      {o.status === 'paid' && <p className="muted small">Downloads sind {o.download_days} Tage nach Zahlung möglich. Bitte nennen Sie bei jeder Veröffentlichung den Urheber.</p>}
      <Proofs order={o} />
    </main>
  )
}

// --- account ------------------------------------------------------------------------------

export function AccountPage() {
  const qc = useQueryClient()
  const { data: me } = useMe()
  const orders = useQuery({ queryKey: ['orders'], queryFn: shop.orders, enabled: !!me?.authenticated })
  const account = useQuery({ queryKey: ['account'], queryFn: shop.account, enabled: !!me?.authenticated })
  const [b, setB] = useState<Billing>(EMPTY_BILLING)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => { if (account.data) setB({ ...EMPTY_BILLING, ...account.data }) }, [account.data])
  const set = (k: keyof Billing) => (e: React.ChangeEvent<HTMLInputElement>) => { setB({ ...b, [k]: e.target.value }); setSaved(false) }

  if (me && !me.authenticated) {
    return <main className="page wrap narrow"><h1>Konto</h1><section className="box"><AuthForm /></section></main>
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      qc.setQueryData(['account'], await shop.saveAccount(b))
      setSaved(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.')
    }
  }

  return (
    <main className="page wrap narrow">
      <h1>Ihr Konto</h1>
      <section className="box">
        <h2>Bestellungen</h2>
        {orders.data?.length === 0 && <p className="muted">Noch keine Bestellungen.</p>}
        {!!orders.data?.length && (
          <table className="list orders">
            <thead><tr><th>Bestellung</th><th>Datum</th><th>Lizenzen</th><th>Betrag</th><th>Status</th></tr></thead>
            <tbody>
              {orders.data.map((o) => (
                <tr key={o.number} onClick={() => navigate(`/bestellungen/${o.number}`)}>
                  <td><Link to={`/bestellungen/${o.number}`} className="mono">{o.number}</Link></td>
                  <td>{date(o.created_at)}</td><td>{o.count}</td><td>{eur(o.gross_cents)}</td>
                  <td><span className={`status ${o.status}`}>{o.status_label}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <form className="box" onSubmit={save}>
        <h2>Rechnungsadresse</h2>
        <div className="fgrid">
          <label className="span2">Firma / Organisation<input value={b.organisation} onChange={set('organisation')} /></label>
          <label>Vorname<input value={b.first_name} onChange={set('first_name')} /></label>
          <label>Nachname<input value={b.last_name} onChange={set('last_name')} /></label>
          <label className="span2">Straße und Hausnummer<input value={b.street} onChange={set('street')} /></label>
          <label>PLZ<input value={b.postal_code} onChange={set('postal_code')} /></label>
          <label>Ort<input value={b.city} onChange={set('city')} /></label>
          <label>Land (ISO)<input value={b.country} onChange={set('country')} maxLength={2} /></label>
          <label>USt-IdNr.<input value={b.vat_id} onChange={set('vat_id')} /></label>
          <label>E-Mail<input type="email" value={b.email} onChange={set('email')} /></label>
          <label>Telefon<input value={b.phone} onChange={set('phone')} /></label>
        </div>
        {error && <p className="err">{error}</p>}
        <div className="cart-actions"><span className="muted small">{saved ? 'Gespeichert.' : ''}</span><button className="btn primary">Speichern</button></div>
      </form>
    </main>
  )
}
